import React from 'react';
import { Box, Typography } from '@mui/material';
import issImage from '../../assets/imgs/iss_main_image.png';
import { styled } from '@mui/material/styles';
import ButtonSecondary from '../../design/button-secondary/ButtonSecondary';
import { useTranslation } from 'react-i18next';

export default function LandingHero() {
  const { t } = useTranslation();

  const handleGetStarted = () => {
    const element = document.querySelector('#services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroSection id='security'>
      <HeaderContainer>
        <ContentWrapper>
          <TitleContainer>
            <HeroTitle>{t('informationServices.hero.title')}</HeroTitle>
          </TitleContainer>

          <SubtitleContainer>
            <DividerLine />
            <HeroSubtitle>
              {t('informationServices.hero.subtitle')}
            </HeroSubtitle>
          </SubtitleContainer>
        </ContentWrapper>
      </HeaderContainer>

      <HeroContainer>
        <DescriptionContainer>
          <HeroDescription>
            {t('informationServices.hero.description')}
          </HeroDescription>
          <ButtonContainer>
            <ButtonSecondary onClick={handleGetStarted}>
              {t('informationServices.hero.buttonText')}
            </ButtonSecondary>
          </ButtonContainer>
        </DescriptionContainer>
      </HeroContainer>
    </HeroSection>
  );
}

const HeroSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '60px 0 0',

  [theme.breakpoints.down('md')]: {
    padding: '40px 0 0',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 9),
  width: '100%',
  display: 'flex',
  justifyContent: 'center',

  '@media (max-width: 1280px)': {
    padding: theme.spacing(3, 4),
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 3),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(8, 2),
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  alignItems: 'flex-start',
  justifyContent: 'center',
  display: 'flex',
  columnGap: theme.spacing(6),

  '@media (max-width: 1280px)': {
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: theme.spacing(3),
  },
  [theme.breakpoints.down('md')]: {
    rowGap: theme.spacing(2),
  },
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: '300px',
  maxWidth: '600px',

  '@media (max-width: 1280px)': {
    width: '100%',
    maxWidth: '100%',
  },
  [theme.breakpoints.down('md')]: {
    minWidth: 'auto',
  },
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 60,
  color: theme.palette.primary.main,

  [theme.breakpoints.down('lg')]: {
    fontSize: 48,
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
  },
}));

const SubtitleContainer = styled(Box)(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: '300px',
  maxWidth: '700px',
  display: 'flex',
  height: '100%',
  columnGap: theme.spacing(4),

  '@media (max-width: 1280px)': {
    width: '100%',
    maxWidth: '100%',
    columnGap: theme.spacing(3),
  },
  [theme.breakpoints.down('md')]: {
    columnGap: theme.spacing(2),
    minWidth: 'auto',
  },
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontWeight: 300,
  fontSize: 24,
  color: theme.palette.primary.main,
  lineHeight: 1.4,
  wordBreak: 'normal',
  whiteSpace: 'normal',

  [theme.breakpoints.down('lg')]: {
    fontSize: 20,
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 18,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 16,
  },
}));

const DividerLine = styled(Box)(({ theme }) => ({
  width: '250px',
  height: '2px',
  marginTop: '16px',
  backgroundColor: theme.palette.primary.main,
  flex: '0 0 auto',

  [theme.breakpoints.down('lg')]: {
    width: '150px',
    marginTop: '12px',
  },
  [theme.breakpoints.down('md')]: {
    width: '80px',
    marginTop: '10px',
  },
}));

const HeroContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: theme.spacing(2, 20),
  background: `url(${issImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: '577px',
  marginRight: '53px',

  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(2, 10),
    minHeight: '500px',
    marginRight: '24px',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4, 3),
    minHeight: '400px',
    marginRight: '0',
    alignItems: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '350px',
    padding: theme.spacing(3, 2),
  },
}));

const DescriptionContainer = styled(Box)(({ theme }) => ({
  width: '300px',
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(2),

  [theme.breakpoints.down('md')]: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    padding: theme.spacing(1.5),
  },
}));

const HeroDescription = styled(Typography)(({ theme }) => ({
  fontWeight: 300,
  fontSize: 14,
  color: theme.palette.background.paper,

  [theme.breakpoints.down('sm')]: {
    fontSize: 13,
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  width: '172px',
  paddingTop: theme.spacing(4),

  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    paddingTop: theme.spacing(1.5),
  },
}));
